import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Check, X, Loader2, Package, Server, Clock, ChevronRight, ChevronDown, FileText } from 'lucide-react';
import { apiService, DeployRequestDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { usePendingRequestsStore } from '@/stores/pendingRequests.store';
import styles from './DeployRequestsPage.module.scss';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Colore un diff git ligne par ligne (+, -, @@). */
function renderDiff(text: string): ReactNode[] {
  return text.split('\n').map((line, i) => {
    const cls = line.startsWith('+') ? styles.diffAdd : line.startsWith('-') ? styles.diffDel : line.startsWith('@@') ? styles.diffMeta : undefined;
    return <div key={i} className={cls}>{line || ' '}</div>;
  });
}

function timeAgo(ts: number): string {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

/** Espace Déploiement : liste des demandes d'édition, avec accepter / refuser + sélection rapide. */
export function DeployRequestsPage() {
  const project = useGameStore((s) => s.activeGameId);
  const decidedBy = useAuthStore((s) => s.user?.gamertag) ?? undefined;
  const [requests, setRequests] = useState<DeployRequestDTO[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rowStatus, setRowStatus] = useState<Record<string, { ok: boolean; msg: string }>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [diffs, setDiffs] = useState<Record<string, string>>({});
  const setPendingCount = usePendingRequestsStore((s) => s.setCount);
  const setProcessing = usePendingRequestsStore((s) => s.setProcessing);
  const fetchPending = usePendingRequestsStore((s) => s.fetch);

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await apiService.listDeployRequests(project);
    const list = r.data?.requests ?? [];
    setRequests(list);
    setPendingCount(list.filter((x) => x.status === 'pending').length);
    setSelected(new Set());
    setLoading(false);
  }, [project, setPendingCount]);

  useEffect(() => { refresh(); }, [refresh]);

  const pending = requests.filter((r) => r.status === 'pending');
  const selectedPending = pending.filter((r) => selected.has(r.id));

  // Regroupe les demandes par cible (même container / même pack) ; groupes avec des en-attente d'abord.
  const grouped = useMemo(() => {
    const map = new Map<string, { key: string; type: string; targetId: string; targetName: string; requests: DeployRequestDTO[] }>();
    for (const r of requests) {
      const key = `${r.type}::${r.targetId}`;
      let g = map.get(key);
      if (!g) { g = { key, type: r.type, targetId: r.targetId, targetName: r.targetName, requests: [] }; map.set(key, g); }
      g.requests.push(r);
    }
    return [...map.values()].sort((a, b) => {
      const ap = a.requests.some((r) => r.status === 'pending') ? 0 : 1;
      const bp = b.requests.some((r) => r.status === 'pending') ? 0 : 1;
      return ap - bp;
    });
  }, [requests]);

  const toggleTargetGroup = (reqs: DeployRequestDTO[]) => {
    const ids = reqs.filter((r) => r.status === 'pending').map((r) => r.id);
    setSelected((s) => {
      const n = new Set(s);
      const allSel = ids.length > 0 && ids.every((id) => n.has(id));
      ids.forEach((id) => (allSel ? n.delete(id) : n.add(id)));
      return n;
    });
  };

  const toggle = (id: string) =>
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Accepte une demande : publication réelle. Pour un container, poll le merge de la PR CI.
  const acceptOne = async (req: DeployRequestDTO) => {
    setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg: 'Publication…' } }));
    const r = await apiService.acceptDeployRequest(req.id, decidedBy);
    const d = r.data;
    if (!d?.success) {
      setRowStatus((s) => ({ ...s, [req.id]: { ok: false, msg: d?.message ?? r.error ?? 'Échec' } }));
      return;
    }
    if (d.needsFinalize && d.containerId && d.version) {
      const cid = d.containerId;
      const version = d.version;
      for (let tries = 1; tries <= 40; tries++) {
        await sleep(6000);
        const f = await apiService.finalizeContainer(cid, version);
        if (f.data?.merged) {
          setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg: `${version} — ${f.data!.message}` } }));
          return;
        }
        if (!f.data?.waiting) {
          setRowStatus((s) => ({ ...s, [req.id]: { ok: false, msg: f.data?.message ?? f.error ?? 'Merge non abouti' } }));
          return;
        }
        setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg: `${version} — merge en cours (${tries})` } }));
      }
      setRowStatus((s) => ({ ...s, [req.id]: { ok: false, msg: `${version} — merge non abouti, vérifie la PR sur GitHub.` } }));
      return;
    }
    setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg: d.message || 'Publié' } }));
  };

  const refuseOne = async (req: DeployRequestDTO) => {
    await apiService.refuseDeployRequest(req.id, decidedBy);
  };

  const toggleExpand = (id: string) =>
    setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const loadDiff = async (req: DeployRequestDTO, file: string) => {
    const key = `${req.id}::${file}`;
    if (diffs[key] !== undefined) return;
    setDiffs((d) => ({ ...d, [key]: '__loading__' }));
    const r = req.type === 'container'
      ? await apiService.getContainerDiff(req.targetId, file)
      : await apiService.getPackDiff(req.targetId, file);
    setDiffs((d) => ({ ...d, [key]: r.data?.diff || '(aucune différence / fichier illisible)' }));
  };

  // Enveloppe un traitement : indicateur global (spinner de nav, survit à la navigation) + refresh du compteur.
  const withProcessing = async (fn: () => Promise<void>) => {
    setBusy(true);
    setProcessing(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      setProcessing(false);
      fetchPending(project); // met à jour le badge même si on a quitté la page
      await refresh();
    }
  };

  const doAccept = (req: DeployRequestDTO) => withProcessing(() => acceptOne(req));
  const doRefuse = (req: DeployRequestDTO) => withProcessing(() => refuseOne(req));

  const runBulk = async (action: 'accept' | 'refuse') => {
    if (selectedPending.length === 0) return;
    if (action === 'refuse' && !window.confirm(`Refuser ${selectedPending.length} demande(s) ?`)) return;
    await withProcessing(async () => {
      if (action === 'refuse') {
        await Promise.all(selectedPending.map((r) => refuseOne(r)));
        return;
      }
      // Accept : parallèle par cible (repos distincts), séquentiel au sein d'une même cible (évite les conflits git).
      const byTarget = new Map<string, DeployRequestDTO[]>();
      for (const r of selectedPending) {
        const k = `${r.type}::${r.targetId}`;
        const arr = byTarget.get(k) ?? [];
        arr.push(r);
        byTarget.set(k, arr);
      }
      await Promise.all([...byTarget.values()].map(async (reqs) => {
        for (const req of reqs) await acceptOne(req);
      }));
    });
  };

  const renderRequest = (req: DeployRequestDTO) => {
    const st = rowStatus[req.id];
    const isPending = req.status === 'pending';
    const rowCls = req.status === 'accepted' ? styles.rowAccepted : req.status === 'refused' ? styles.rowRefused : '';
    const chipCls = req.status === 'pending' ? styles.chipPending : req.status === 'accepted' ? styles.chipAccepted : styles.chipRefused;
    const chipTxt = req.status === 'pending' ? 'En attente' : req.status === 'accepted' ? 'Acceptée' : 'Refusée';
    const isExp = expanded.has(req.id);
    return (
      <div key={req.id} className={`${styles.item} ${rowCls}`}>
        <div className={styles.row}>
          <input type="checkbox" disabled={!isPending || busy} checked={selected.has(req.id)} onChange={() => toggle(req.id)} />
          <button className={styles.expandBtn} onClick={() => toggleExpand(req.id)} title="Voir le détail">
            {isExp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className={styles.info}>
            <div className={styles.target}>
              {req.files.length} fichier{req.files.length > 1 ? 's' : ''}
              {req.message ? <span className={styles.fileCount}> · « {req.message} »</span> : null}
            </div>
            <div className={styles.meta}>
              {req.requestedBy} · <Clock size={11} /> {timeAgo(req.createdAt)}
            </div>
            {st && <div className={st.ok ? styles.rowOk : styles.rowErr}>{st.msg}</div>}
            {!isPending && !st && req.result && (
              <div className={styles.muted}>{req.status === 'accepted' ? '✓ ' : '✕ '}{req.result}{req.decidedBy ? ` (${req.decidedBy})` : ''}</div>
            )}
          </div>
          <span className={`${styles.statusChip} ${chipCls}`}>{chipTxt}</span>
          {isPending && (
            <div className={styles.rowActions}>
              <button className={styles.acceptBtn} disabled={busy} onClick={() => doAccept(req)}><Check size={14} /> Accepter</button>
              <button className={styles.refuseBtn} disabled={busy} onClick={() => doRefuse(req)}><X size={14} /> Refuser</button>
            </div>
          )}
        </div>

        {isExp && (
          <div className={styles.detail}>
            {req.files.length === 0 ? (
              <div className={styles.muted}>Aucun fichier.</div>
            ) : req.files.map((f) => {
              const key = `${req.id}::${f}`;
              const diff = diffs[key];
              return (
                <div key={f} className={styles.fileBlock}>
                  <button className={styles.fileName} onClick={() => loadDiff(req, f)}>
                    <FileText size={13} /> {f}
                  </button>
                  {diff === '__loading__' ? (
                    <div className={styles.muted}>Chargement du diff…</div>
                  ) : diff !== undefined ? (
                    <pre className={styles.diffPre}>{renderDiff(diff)}</pre>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Demandes d'édition</h1>
          <p className={styles.subtitle}>Accepte (publication réelle) ou refuse les demandes d'envoi. Sélection rapide pour traiter en lot.</p>
        </div>
        <button className={styles.iconBtn} onClick={refresh} disabled={loading || busy} title="Rafraîchir">
          <RefreshCw size={16} className={loading ? styles.spin : undefined} />
        </button>
      </header>

      {pending.length > 0 && (
        <div className={styles.bulkBar}>
          <label className={styles.selAll}>
            <input
              type="checkbox"
              checked={selectedPending.length > 0 && selectedPending.length === pending.length}
              onChange={(e) => setSelected(e.target.checked ? new Set(pending.map((r) => r.id)) : new Set())}
            />
            Tout sélectionner ({pending.length} en attente)
          </label>
          <span className={styles.spacer} />
          <button className={styles.acceptBtn} disabled={busy || selectedPending.length === 0} onClick={() => runBulk('accept')}>
            {busy ? <Loader2 size={14} className={styles.spin} /> : <Check size={14} />} Accepter ({selectedPending.length})
          </button>
          <button className={styles.refuseBtn} disabled={busy || selectedPending.length === 0} onClick={() => runBulk('refuse')}>
            <X size={14} /> Refuser ({selectedPending.length})
          </button>
        </div>
      )}

      {loading ? (
        <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>
      ) : requests.length === 0 ? (
        <div className={styles.center}>Aucune demande d'édition.</div>
      ) : (
        <div className={styles.list}>
          {grouped.map((g) => {
            const gPending = g.requests.filter((r) => r.status === 'pending');
            const allGroupSel = gPending.length > 0 && gPending.every((r) => selected.has(r.id));
            return (
              <div key={g.key} className={styles.targetGroup}>
                <div className={styles.targetHead}>
                  {gPending.length > 0 && (
                    <input type="checkbox" checked={allGroupSel} disabled={busy} onChange={() => toggleTargetGroup(g.requests)} />
                  )}
                  <span className={styles.typeBadge}>
                    {g.type === 'container' ? <Server size={13} /> : <Package size={13} />} {g.type === 'container' ? 'Container' : 'Pack'}
                  </span>
                  <span className={styles.targetName}>{g.targetName}</span>
                  <span className={styles.groupCount}>
                    {g.requests.length} demande{g.requests.length > 1 ? 's' : ''}{gPending.length ? ` · ${gPending.length} en attente` : ''}
                  </span>
                </div>
                <div className={styles.targetBody}>
                  {g.requests.map(renderRequest)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
